import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Volume2, Mic, SkipForward } from "lucide-react";

interface AudioCheckPromptProps {
  open: boolean;
  onSkip: () => void;
  onConfirm: (options: { micDeviceId?: string; speakerDeviceId?: string }) => void;
  defaultMicId?: string;
  defaultSpeakerId?: string;
}

const createBeepUrl = () => {
  const sampleRate = 44100;
  const duration = 0.5;
  const numSamples = sampleRate * duration;
  const amplitude = 0.2 * 32767;
  const buffer = new ArrayBuffer(44 + numSamples * 2);
  const view = new DataView(buffer);

  const writeString = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  };

  writeString(0, "RIFF");
  view.setUint32(4, 36 + numSamples * 2, true);
  writeString(8, "WAVE");
  writeString(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(36, "data");
  view.setUint32(40, numSamples * 2, true);

  const frequency = 880;
  for (let i = 0; i < numSamples; i++) {
    const t = i / sampleRate;
    const sample = amplitude * Math.sin(2 * Math.PI * frequency * t);
    view.setInt16(44 + i * 2, sample, true);
  }

  const blob = new Blob([buffer], { type: "audio/wav" });
  return URL.createObjectURL(blob);
};

export function AudioCheckPrompt({
  open,
  onSkip,
  onConfirm,
  defaultMicId,
  defaultSpeakerId,
}: AudioCheckPromptProps) {
  const [availableMics, setAvailableMics] = useState<MediaDeviceInfo[]>([]);
  const [availableSpeakers, setAvailableSpeakers] = useState<MediaDeviceInfo[]>([]);
  const [selectedMicId, setSelectedMicId] = useState<string | undefined>(defaultMicId);
  const [selectedSpeakerId, setSelectedSpeakerId] = useState<string | undefined>(defaultSpeakerId);
  const [micLevel, setMicLevel] = useState(0);
  const micStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const animationFrameRef = useRef<number>();
  const beepUrl = useMemo(() => createBeepUrl(), []);

  useEffect(() => {
    return () => {
      URL.revokeObjectURL(beepUrl);
    };
  }, [beepUrl]);

  const stopMicStream = () => {
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(track => track.stop());
      micStreamRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = undefined;
    }
  };

  useEffect(() => {
    if (!open) {
      stopMicStream();
      return;
    }

    const loadDevices = async () => {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const mics = devices.filter(device => device.kind === "audioinput");
      const speakers = devices.filter(device => device.kind === "audiooutput");
      setAvailableMics(mics);
      setAvailableSpeakers(speakers);

      if (!selectedMicId && (defaultMicId || mics[0])) {
        setSelectedMicId(defaultMicId || mics[0]?.deviceId);
      }
      if (!selectedSpeakerId && (defaultSpeakerId || speakers[0])) {
        setSelectedSpeakerId(defaultSpeakerId || speakers[0]?.deviceId);
      }
    };

    loadDevices();
  }, [defaultMicId, defaultSpeakerId, open, selectedMicId, selectedSpeakerId]);

  useEffect(() => {
    if (!open) return;

    const startMicMonitor = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            ...(selectedMicId ? { deviceId: { exact: selectedMicId } } : {}),
          },
          video: false,
        });

        micStreamRef.current?.getTracks().forEach(track => track.stop());
        micStreamRef.current = stream;

        const audioContext = new AudioContext();
        audioContextRef.current = audioContext;
        const source = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);

        const dataArray = new Uint8Array(analyser.frequencyBinCount);

        const updateLevel = () => {
          analyser.getByteFrequencyData(dataArray);
          const average = dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;
          setMicLevel(Math.min(100, Math.round((average / 255) * 100)));
          animationFrameRef.current = requestAnimationFrame(updateLevel);
        };

        updateLevel();
      } catch (error) {
        console.error("Error starting mic monitor", error);
      }
    };

    startMicMonitor();

    return () => {
      stopMicStream();
    };
  }, [open, selectedMicId]);

  const playTestTone = async () => {
    const audio = new Audio(beepUrl);
    try {
      if ((audio as any).setSinkId && selectedSpeakerId) {
        await (audio as any).setSinkId(selectedSpeakerId);
      }
      await audio.play();
    } catch (error) {
      console.error("Error playing test tone", error);
    }
  };

  const handleConfirm = () => {
    onConfirm({ micDeviceId: selectedMicId, speakerDeviceId: selectedSpeakerId });
  };

  return (
    <Dialog open={open} onOpenChange={(value) => !value && onSkip()}>
      <DialogContent className="w-[95vw] max-w-lg p-4 sm:p-6">
        <DialogHeader className="space-y-2">
          <DialogTitle className="text-lg sm:text-xl">Quick audio check</DialogTitle>
          <DialogDescription className="text-sm sm:text-base">
            Make sure you can hear others and they can hear you. You can skip if you’ve already verified.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-3 sm:gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Volume2 className="h-4 w-4" /> Speaker
              </Label>
              <select
                className="w-full rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                value={selectedSpeakerId || ""}
                onChange={(event) => setSelectedSpeakerId(event.target.value || undefined)}
              >
                {availableSpeakers.map(device => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label || "Speaker"}
                  </option>
                ))}
                {availableSpeakers.length === 0 && <option value="">No speaker found</option>}
              </select>
              <Button variant="outline" onClick={playTestTone} className="w-full text-sm sm:text-base">
                Play test tone
              </Button>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Mic className="h-4 w-4" /> Microphone
              </Label>
              <select
                className="w-full rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                value={selectedMicId || ""}
                onChange={(event) => setSelectedMicId(event.target.value || undefined)}
              >
                {availableMics.map(device => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label || "Microphone"}
                  </option>
                ))}
                {availableMics.length === 0 && <option value="">No microphone found</option>}
              </select>
              <div>
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                  <span>Input level</span>
                  <span>{micLevel}%</span>
                </div>
                <Progress value={micLevel} className="h-2" />
              </div>
            </div>
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2">
            <Button variant="ghost" onClick={onSkip} className="gap-2 w-full sm:w-auto text-sm sm:text-base">
              <SkipForward className="h-4 w-4" /> Skip for now
            </Button>
            <Button onClick={handleConfirm} className="w-full sm:w-auto text-sm sm:text-base">Confirm devices</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
